import { getInteractionName, getHandlingStatusIcon } from "@/common/helpers";
import { Flex, GapSize } from "@/components/atoms/flex";
import { useCallback, useMemo, useState } from "react";
import {
	HistoryListMobileProps,
	HistoryTableColumnName,
	InteractionHistoryListItemDto,
} from "./types";
import { List } from "@/components/atoms/list";
import { formatDate } from "@/common/helpers/dateHelper";
import { ItemProp } from "@/components/atoms/itemDetailsMobile";
import { useIntl } from "react-intl";
import { ItemDetailsDialogMobile } from "@/components/molecules/itemDetailsDialogMobile";
import { Text } from "@fluentui/react-components";
import { useStyle } from "./style";
import { getHistoryInteractionIcon } from "@/common/helpers/interactionIconHelper";
import { TagStack } from "@/components/atoms/tagStack";
import { useTagsExist } from "@/common/stores/persistingStores/app/appSettingsStore";

export const HistoryListMobile = (props: HistoryListMobileProps) => {
	const { historyTableItems, getItemActions, infiniteScrollSpinner } = props;
	const intl = useIntl();
	const classes = useStyle();

	const tagsExist = useTagsExist();

	const [isDetailsDialogOpen, setIsDetailsDialogOpen] =
		useState<boolean>(false);

	const [selectedItemActions, setSelectedItemActions] =
		useState<JSX.Element[]>();

	const [itemDetailsList, setItemDetailsList] = useState<ItemProp[]>();

	const createDetail = (
		labelId: string,
		mainValue: any,
		secondaryValue?: any
	) => ({
		label: intl.formatMessage({ id: `History.${labelId}` }),
		mainValue,
		secondaryValue,
	});

	const openActiveInteractionDetailsDialog = useCallback(
		(
			item: InteractionHistoryListItemDto,
			actions: (JSX.Element | undefined)[]
		) => {
			setItemDetailsList([
				createDetail(HistoryTableColumnName.Name, item.name, item.contactInfo),
				createDetail(HistoryTableColumnName.QueueName, item.queueName),
				createDetail(HistoryTableColumnName.AgentName, item.agentName),
				createDetail(
					HistoryTableColumnName.IsHandled,
					getHandlingStatusIcon(item.isHandled)
				),
				createDetail(
					HistoryTableColumnName.StartTime,
					item.arrivalTime ? formatDate(item.arrivalTime) : ""
				),
				createDetail(
					HistoryTableColumnName.EndTime,
					item.endTime ? formatDate(item.endTime) : ""
				),
				createDetail(
					HistoryTableColumnName.HandlingTime,
					item.handlingTimeFormatted
				),
				// Dropped entirely rather than shown as a dash when the tenant has no tag configured, so
				// mobile matches the desktop table, which excludes the column outright.
				...(tagsExist
					? [
							createDetail(
								HistoryTableColumnName.Tags,
								<TagStack tags={item.assignedTags} />
							),
						]
					: []),
			]);

			if (actions) {
				setSelectedItemActions(actions.filter(Boolean));
			}

			setIsDetailsDialogOpen(true);
		},
		[tagsExist]
	);

	const listItems = useMemo(() => {
		return historyTableItems
			? historyTableItems.map((historyItem) => {
					return {
						key: historyItem.id,
						media: getHistoryInteractionIcon(historyItem),
						header: getInteractionName(
							historyItem.type,
							historyItem.name,
							intl
						),
						subHeader: historyItem.subject,
						headerMedia: (
							<Flex column gap={GapSize.Medium} vAlign="flex-end">
								{getHandlingStatusIcon(historyItem.isHandled)}
								<Text>{historyItem.handlingTimeFormatted}</Text>
							</Flex>
						),
						onClick: (_: any) => {
							openActiveInteractionDetailsDialog(
								historyItem,
								getItemActions(historyItem)
							);
						},
					};
				})
			: [];
	}, [historyTableItems]);

	return (
		<>
			<div>
				<List
					items={listItems}
					classesName={{ item: classes.listItem, mainText: classes.listHeader }}
				></List>
				{infiniteScrollSpinner}
			</div>
			<ItemDetailsDialogMobile
				itemProps={itemDetailsList}
				itemActions={selectedItemActions}
				titleKey={"MobileView.HistoryInteractionDetails"}
				open={isDetailsDialogOpen}
				setOpen={setIsDetailsDialogOpen}
			/>
		</>
	);
};
